import React, { useState, useRef, useEffect, useMemo, useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { BdsIcon } from '@/core/constants/webComponents';
import { normalizeString as normalize } from '@/core/utils/normalizeString';

interface ModalSearchableDropdownProps {
  readonly placeholder: string;
  readonly inputValue: string;
  readonly onInputChange: (value: string) => void;
  readonly suggestedItems: readonly string[];
  readonly loading?: boolean;
  readonly onAddClick: (item: string) => void;
  readonly initiallyOpen?: boolean;
  readonly fullWidth?: boolean;
  readonly compact?: boolean;
  readonly isTag?: boolean;
  readonly onCreateTag?: (name: string) => void;
}

export const ModalSearchableDropdown: React.FC<ModalSearchableDropdownProps> = ({
  placeholder,
  inputValue,
  onInputChange,
  suggestedItems,
  loading = false,
  onAddClick,
  initiallyOpen = true,
  fullWidth = false,
  compact = false,
  isTag = false,
  onCreateTag,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const listboxId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isInitialFocusRef = useRef(true);

  useEffect(() => {
    inputRef.current?.focus();
    isInitialFocusRef.current = false;
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const filtered = useMemo(() => {
    const q = normalize(inputValue.trim());
    if (!q) return suggestedItems;
    return suggestedItems.filter((item) => normalize(item).includes(q));
  }, [suggestedItems, inputValue]);

  const exactMatch = useMemo(() => {
    const q = normalize(inputValue.trim());
    return q ? suggestedItems.some((item) => normalize(item) === q) : false;
  }, [suggestedItems, inputValue]);

  const handleItemClick = useCallback((item: string) => {
    onAddClick(item);
    setIsOpen(true);
    inputRef.current?.focus();
  }, [onAddClick]);

  const handleInputClick = () => {
    setIsOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && filtered.length > 0) {
      e.preventDefault();
      handleItemClick(filtered[0]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
      const first = dropdownRef.current?.querySelector('button') as HTMLButtonElement | null;
      first?.focus();
    }
  };

  const handleOptionKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleItemClick(filtered[index]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const buttons = Array.from(dropdownRef.current?.querySelectorAll('button') ?? []);
      if (index + 1 < buttons.length) {
        buttons[index + 1].focus();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (index === 0) {
        inputRef.current?.focus();
      } else {
        const buttons = Array.from(dropdownRef.current?.querySelectorAll('button') ?? []);
        buttons[index - 1].focus();
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const renderDropdownContent = () => {
    if (loading) {
      return (
        <div className="modal-search-dropdown__loading">
          <span className="modal-search-dropdown__spinner" aria-hidden="true" />
          <span>{t('classificationItemModal.loading')}</span>
        </div>
      );
    }
    if (filtered.length === 0 && !isTag) {
      return (
        <div className="modal-search-dropdown__empty">
          {t('classificationItemModal.emptySearch')}
        </div>
      );
    }
    return (
      <>
        {filtered.map((item, index) => (
          <button
            key={item}
            type="button"
            className="modal-search-dropdown__option"
            onClick={() => handleItemClick(item)}
            onKeyDown={(e) => handleOptionKeyDown(e, index)}
          >
            {item}
          </button>
        ))}
        {isTag && inputValue.trim() && !exactMatch && (
          <button
            type="button"
            className="modal-search-dropdown__create-button"
            onClick={() => {
              onCreateTag?.(inputValue.trim());
              inputRef.current?.focus();
            }}
          >
            <BdsIcon name="add" theme="outline" color="white" size="small" />
            <span className="modal-search-dropdown__create-label">
              {t('classificationCard.createTag', { tag: `"${inputValue.trim()}"` })}
            </span>
          </button>
        )}
      </>
    );
  };

  const rootClassName = [
    'modal-search-dropdown',
    fullWidth ? 'modal-search-dropdown--full-width' : '',
    compact ? 'modal-search-dropdown--compact' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={containerRef} className={rootClassName}>
      <div className="modal-search-dropdown__input-wrapper">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          className="modal-search-dropdown__input"
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => {
            onInputChange(e.target.value);
            setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            // Only open the dropdown on focus if this isn't the initial programmatic focus (unless initiallyOpen is true)
            if (!isInitialFocusRef.current || initiallyOpen) {
              setIsOpen(true);
            }
          }}
          onClick={handleInputClick}
        />
        <BdsIcon
          name={isOpen ? 'arrow-up' : 'arrow-down'}
          size="small"
          class="modal-search-dropdown__arrow-icon"
        />
        {inputValue && !loading && (
          <button
            type="button"
            className="modal-search-dropdown__clear"
            onClick={() => {
              onInputChange('');
              inputRef.current?.focus();
            }}
            aria-label={t('globalFilter.clear')}
          >
            <BdsIcon name="close" size="small" />
          </button>
        )}
      </div>

      <select
        id={listboxId}
        multiple
        aria-label={placeholder}
        style={{ display: 'none' }}
        tabIndex={-1}
      >
        {filtered.map((item) => (
          <option key={item} value={item}>{item}</option>
        ))}
      </select>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="modal-search-dropdown__options"
        >
          {renderDropdownContent()}
        </div>
      )}
    </div>
  );
};
